import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatIST } from '@/lib/utils';
import { STATUS_TONE } from './documentTypes';
import { rupees } from './totals';

/**
 * The same documents, banked by customer.
 *
 * "How much have we quoted this firm, and when did we last send them anything"
 * is a question the flat list can only answer by filtering and adding up. The
 * grouped view answers it in the section header — count, total value, last
 * document — and the documents themselves stay one click away underneath.
 */
export default function CustomerGroups({ groups, loading, onOpen }) {
  const [open, setOpen] = useState(() => new Set());

  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <p className="py-10 text-center text-xs text-slate-400">No documents match these filters</p>;
  }

  return (
    <div className="divide-y divide-slate-100" data-testid="customer-groups">
      {groups.map((group) => {
        const key = group.customer_id || group.customer_name;
        const expanded = open.has(key);
        return (
          <section key={key} data-testid={`customer-group-${key}`}>
            <button
              type="button"
              onClick={() => toggle(key)}
              aria-expanded={expanded}
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors hover:bg-slate-50"
            >
              <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-brand-blue">
                {group.customer_name || 'Unnamed party'}
              </span>
              <span className="shrink-0 text-[11px] text-slate-500">
                {group.count} {group.count === 1 ? 'document' : 'documents'}
              </span>
              <span className="shrink-0 font-mono text-[12px] font-semibold tabular-nums text-doc-navy">
                {rupees(group.total_value)}
              </span>
              <span className="hidden shrink-0 text-[11px] text-slate-400 sm:inline"
                title={formatIST(group.last_created_at)}>
                last {formatIST(group.last_created_at, { dateOnly: true })}
              </span>
            </button>

            {expanded && (
              <table className="w-full border-collapse bg-slate-50/60 text-[12px]">
                <tbody>
                  {(group.documents || []).map((document) => (
                    <tr
                      key={document.id}
                      onClick={() => onOpen(document)}
                      className="cursor-pointer border-t border-slate-100 hover:bg-white"
                      data-testid={`group-document-${document.id}`}
                    >
                      <td className="w-40 py-1.5 pl-9 pr-2 font-mono text-[11px] font-semibold text-doc-navy">
                        {document.number}
                      </td>
                      <td className="w-24 px-2 text-slate-500">{formatIST(document.date, { dateOnly: true })}</td>
                      <td className="truncate px-2 text-slate-600">{document.specs?.product_name || '—'}</td>
                      <td className="w-28 px-2 text-right font-mono tabular-nums text-slate-700">
                        {rupees(document.totals?.grand_total)}
                      </td>
                      <td className={`w-24 px-2 text-right font-medium ${STATUS_TONE[document.status] || 'text-slate-500'}`}>
                        {document.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
}
